import {
  FirebaseConnectionContext,
  FirebaseConnectionOutcome,
  FirebaseConnectionStatus,
} from './FirebaseConnection.enum';

export const DEFAULT_FIREBASE_CONNECTION_TIMEOUT_MS = 10_000;
export const FIREBASE_CONNECTION_STATUS_ATTRIBUTE = 'data-pl-firebase-connection';

export interface FirebaseConnectionTelemetry {
  event: 'firebase_rtdb_connection';
  outcome: FirebaseConnectionOutcome;
  duration_ms: number;
  connection_context: FirebaseConnectionContext;
  timed_out: boolean;
  visibility_state: DocumentVisibilityState | 'unknown';
  chat_id?: number;
  error_name?: string;
  error_message?: string;
}

interface FirebaseConnectionMonitorOptions {
  connectionContext: FirebaseConnectionContext;
  timeoutMs?: number;
  chatId?: number;
  now?: () => number;
}

type FirebaseConnectionStatusListener = (event: FirebaseConnectionTelemetry) => void;

declare const GLOBAL_FIREBASE_CONNECTION_TIMEOUT_MS: number;

export function getFirebaseConnectionTimeoutMs(rawValue?: string | number): number {
  const configuredValue =
    rawValue ??
    (typeof GLOBAL_FIREBASE_CONNECTION_TIMEOUT_MS === 'number' ? GLOBAL_FIREBASE_CONNECTION_TIMEOUT_MS : undefined);
  const value = Number(configuredValue);
  return Number.isFinite(value) && value > 0 ? value : DEFAULT_FIREBASE_CONNECTION_TIMEOUT_MS;
}

/**
 * Expose the active RTDB listener state to browser synthetics. This is a DOM
 * monitoring contract only; it does not create another Firebase reference or
 * expose Firebase credentials.
 */
export function setFirebaseConnectionStatus(status: FirebaseConnectionStatus): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute(FIREBASE_CONNECTION_STATUS_ATTRIBUTE, status);
}

function getErrorDetails(error: unknown): Pick<FirebaseConnectionTelemetry, 'error_name' | 'error_message'> {
  if (error instanceof Error) {
    return { error_name: error.name, error_message: error.message };
  }

  return { error_name: 'Error', error_message: String(error) };
}

export class FirebaseConnectionMonitor {
  private readonly startedAt: number;
  private readonly now: () => number;
  private readonly timeout: ReturnType<typeof setTimeout>;
  private readonly statusChangeListeners = new Set<FirebaseConnectionStatusListener>();
  private completed = false;
  private timedOut = false;

  constructor(private readonly options: FirebaseConnectionMonitorOptions) {
    this.now = options.now ?? (() => performance.now());
    this.startedAt = this.now();
    this.timeout = setTimeout(() => this.handleTimeout(), options.timeoutMs ?? DEFAULT_FIREBASE_CONNECTION_TIMEOUT_MS);
  }

  onStatusChange(listener: FirebaseConnectionStatusListener): () => void {
    if (this.completed) return () => undefined;

    this.statusChangeListeners.add(listener);

    return () => {
      this.statusChangeListeners.delete(listener);
    };
  }

  observe(connected: boolean): void {
    if (!connected || this.completed) return;

    this.complete(FirebaseConnectionOutcome.Connected);
  }

  fail(error: unknown): void {
    if (this.completed) return;

    this.complete(FirebaseConnectionOutcome.Failed, getErrorDetails(error));
  }

  cancel(): void {
    if (!this.completed) {
      this.completed = true;
      clearTimeout(this.timeout);
    }

    this.statusChangeListeners.clear();
  }

  private handleTimeout(): void {
    if (this.completed || this.timedOut) return;
    this.timedOut = true;
    this.notifyStatusChange(FirebaseConnectionOutcome.Timeout);
  }

  private complete(
    outcome: FirebaseConnectionOutcome.Connected | FirebaseConnectionOutcome.Failed,
    details: Pick<FirebaseConnectionTelemetry, 'error_name' | 'error_message'> = {}
  ): void {
    this.completed = true;
    clearTimeout(this.timeout);

    try {
      this.notifyStatusChange(outcome, details);
    } finally {
      this.statusChangeListeners.clear();
    }
  }

  private notifyStatusChange(
    outcome: FirebaseConnectionOutcome,
    details: Pick<FirebaseConnectionTelemetry, 'error_name' | 'error_message'> = {}
  ): void {
    const event: FirebaseConnectionTelemetry = {
      event: 'firebase_rtdb_connection',
      outcome,
      duration_ms: Math.max(0, Math.round(this.now() - this.startedAt)),
      connection_context: this.options.connectionContext,
      timed_out: this.timedOut,
      visibility_state: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
      ...(this.options.chatId == null ? {} : { chat_id: this.options.chatId }),
      ...details,
    };

    this.statusChangeListeners.forEach((listener) => listener(event));
  }
}
